import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { uploadFile } from "@/lib/upload13";

const KEYS = ["nickname", "make", "model", "year", "trim", "plate", "color", "tire_size"];

export default function VehicleDialog({ base, v, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...Object.fromEntries(KEYS.map((k) => [k, v?.[k] ?? ""])), vin: "", odometer: v?.odometer ?? "",
    registration_date: v?.registration_date || "", inspection_date: v?.inspection_date || "", insurance_date: v?.insurance_date || "" }));
  const [vinMsg, setVinMsg] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setF((x) => ({ ...x, [k]: val }));
  const decode = async (vin) => {
    setVinMsg("");
    if (!vin) return;
    setDecoding(true);
    try {
      const { data } = await api.get(`${base}/decode`, { params: { vin } });
      setF((x) => ({ ...x, make: data.make, model: data.model, year: data.year, trim: data.trim || x.trim, nickname: x.nickname || data.model }));
      setVinMsg("ok");
    } catch (e) { setVinMsg(errMsg(e)); } finally { setDecoding(false); }
  };
  const onVin = (val) => { const u = val.toUpperCase().replace(/\s/g, ""); set("vin", u); if (u.length === 17) decode(u); else setVinMsg(""); };
  const save = async () => {
    setBusy(true);
    try {
      const photo_upload_id = photo ? await uploadFile(base, photo) : null;
      const body = { ...f, year: f.year ? Number(f.year) : null, odometer: f.odometer === "" ? null : Number(f.odometer), vin: f.vin || null, photo_upload_id,
        registration_date: f.registration_date || null, inspection_date: f.inspection_date || null, insurance_date: f.insurance_date || null };
      const { data } = v ? await api.put(`${base}/${v.id}`, body) : await api.post(base, body);
      toast.success("Saved"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inp = (k, l, t = "text", ph = "") => <Field label={l}><input type={t} className="q-input min-h-[44px] w-full" value={f[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`vehicle-${k}`} /></Field>;
  return (
    <FormDialog open onClose={onClose} title={v ? `Edit ${v.nickname}` : "Add a vehicle"} description="Type the 17-character VIN and we'll fill in the details from NHTSA." testid="vehicle-dialog">
      <div className="space-y-3">
        <Field label={v?.has_vin ? `VIN (on file: ${v.vin_masked}; type a new one to change)` : "VIN"}>
          <div className="relative"><input className="q-input min-h-[44px] w-full font-mono uppercase" maxLength={17} value={f.vin} onChange={(e) => onVin(e.target.value)}
            onBlur={() => f.vin && f.vin.length !== 17 && decode(f.vin)} placeholder="17 characters" data-testid="vehicle-vin" />
            {decoding && <Loader2 className="absolute right-3 top-3 h-5 w-5 animate-spin text-q-muted" />}</div>
        </Field>
        {vinMsg && vinMsg !== "ok" && <p className="q-error" data-testid="vehicle-vin-error">{vinMsg}</p>}
        {vinMsg === "ok" && <p className="text-xs text-q-muted" data-testid="vehicle-vin-ok">Filled in from NHTSA.</p>}
        {inp("nickname", "Nickname", "text", "CR-V")}
        <div className="grid grid-cols-2 gap-3">
          {inp("make", "Make")}{inp("model", "Model")}{inp("year", "Year", "number")}{inp("trim", "Trim")}
          {inp("plate", "Plate")}{inp("color", "Color")}{inp("tire_size", "Tire size", "text", "235/65R17")}
          {!v && inp("odometer", "Odometer (mi)", "number")}
          {inp("registration_date", "Registration renews", "date")}{inp("inspection_date", "Inspection due", "date")}{inp("insurance_date", "Insurance renews", "date")}
        </div>
        <Field label="Photo (optional)"><input type="file" accept="image/jpeg,image/png,image/heic,.jpg,.jpeg,.png,.heic" className="q-input min-h-[44px] w-full" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="vehicle-photo" /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy || !String(f.nickname).trim()} onClick={save} data-testid="vehicle-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </FormDialog>
  );
}
